// src/components/Player.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import apiService from '../services/api';
import StorageService from '../utils/storage';
import browserCheck from '../utils/browserCheck';
import streamTester from '../utils/streamTester';
import mpegts from 'mpegts.js';

const PlayerContainer = styled.div`
  position: relative;
  width: 100%;
  background-color: #000;
  
  .react-player {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const AspectRatioBox = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  overflow: hidden;
  background-color: #000;
`;

const PlayerControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 100%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;

  ${PlayerContainer}:hover & {
    opacity: 1;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  
  &:hover {
    height: 6px;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${props => props.$progress}%;
    background-color: #E50914;
    border-radius: 2px;
  }
`;

const ControlButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: scale(1.1);
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  &.large-icon svg {
    width: 32px;
    height: 32px;
  }
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 150px;
  
  input[type="range"] {
    flex: 1;
    -webkit-appearance: none;
    height: 4px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.2);
    outline: none;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    &::-webkit-slider-thumb:hover {
      transform: scale(1.2);
    }
  }
`;

const ChannelInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  color: white;
  
  .channel-logo {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
  
  .channel-details {
    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 500;
    }
    
    p {
      margin: 4px 0 0;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.7);
    }
  }
`;

const QualityBadge = styled.span`
  background-color: ${props => {
    switch(props.$quality) {
      case 'SD': return '#666';
      case 'HD': return '#4caf50';
      case 'FHD': return '#2196f3';
      case 'FHD H265': return '#1976d2';
      case 'FHD HDR': return '#0d47a1';
      case '4K': return '#E50914';
      case '4K H265': return '#b71c1c';
      case '4K HDR': return '#7f0000';
      default: return '#666';
    }
  }};
  color: white;
  font-size: 12px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 2px;
`;

const BufferingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
  color: white;
  z-index: 100;

  svg {
    width: 120px;
    height: 120px;
  }

  p {
    font-size: 16px;
    font-weight: 500;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: white;
  text-align: center;
  
  h3 {
    color: #E50914;
    margin-bottom: 15px;
  }
  
  .error-buttons {
    display: flex;
    gap: 10px;
    margin-top: 20px;
  }
  
  button {
    background: #E50914;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
      background: #F40D17;
    }
    
    &.secondary {
      background: rgba(255, 255, 255, 0.1);
      
      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }
`;

const Player = React.forwardRef(({ 
  channelId, 
  detectQuality, 
  isPlaying,
  volume,
  onProgress,
  onDuration
}, ref) => {
  const [channel, setChannel] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useAlternativePlayer, setUseAlternativePlayer] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [streamFormats, setStreamFormats] = useState(browserCheck.getSupportedFormats());
  const [currentFormatIndex, setCurrentFormatIndex] = useState(0);
  const [streamUrl, setStreamUrl] = useState('');
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const mpegtsPlayerRef = useRef(null);
  const lastAttemptTime = useRef(Date.now());
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [streamTestResults, setStreamTestResults] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [currentServerIndex, setCurrentServerIndex] = useState(0);
  const [hasTestedAllServers, setHasTestedAllServers] = useState(false);
  const [urlTested, setUrlTested] = useState(false);
  const attemptedServers = useRef(new Set());
  const loadingAttempted = useRef(false);
  const [isFixedErrorState, setIsFixedErrorState] = useState(false);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const MAX_TOTAL_ATTEMPTS = 8;
  const [initialLoading, setInitialLoading] = useState(true);

  // Expõe funções para o componente pai
  React.useImperativeHandle(ref, () => ({
    seekTo: (seconds) => {
      if (playerRef.current) {
        playerRef.current.seekTo(seconds, 'fraction');
      } else if (videoRef.current) {
        videoRef.current.currentTime = seconds * videoRef.current.duration;
      }
    },
    showDiagnostics: () => setShowDiagnostics(true)
  }));

  const loadChannel = useCallback(async (forceReload = false) => {
    if (loadingAttempted.current && !forceReload) {
      const timeSinceLastAttempt = Date.now() - lastAttemptTime.current;
      if (timeSinceLastAttempt < 1000) {
        console.log('Tentativa muito recente, aguardando...');
        return;
      }
    }

    loadingAttempted.current = true;
    lastAttemptTime.current = Date.now();
    setInitialLoading(true);

    try {
      setIsLoading(true);
      setError(null);
      setStreamTestResults(null);
      setStreamUrl('');
      setRetryCount(0);
      setCurrentFormatIndex(0);
      setCurrentServerIndex(0);
      apiService.resetServer();
      attemptedServers.current = new Set();
      setHasTestedAllServers(false);
      setUrlTested(false);
      setIsFixedErrorState(false);

      if (!apiService.isLoaded) {
        await apiService.loadChannels();
      }

      const channelData = apiService.getChannel(channelId);
      if (!channelData) {
        throw new Error(`Canal não encontrado: ${channelId}`);
      }

      setTotalAttempts(prevAttempts => prevAttempts + 1);

      setChannel(channelData);
      StorageService.addRecentChannel(channelData);

      const initialUrl = channelData.url;
      console.log(`URL original do canal: ${initialUrl}`);
      setStreamUrl(initialUrl);

      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsLoading(false);
      setInitialLoading(false);
    } catch (err) {
      setIsLoading(false);
      setInitialLoading(false);
      setError(err.message || 'Erro ao carregar o canal');
      console.error('Erro ao carregar canal:', err);
      setIsFixedErrorState(true);
    } finally {
      setTimeout(() => {
        loadingAttempted.current = false;
      }, 1500);
    }
  }, [channelId, currentServerIndex, totalAttempts]);

  useEffect(() => {
    if (!isFixedErrorState) {
      loadChannel();
    }
  }, [channelId, loadChannel, isFixedErrorState]);

  const testStreamUrls = useCallback(async (forceTest = false) => {
    if (!channel || (isTesting && !forceTest)) return;

    if (totalAttempts >= MAX_TOTAL_ATTEMPTS && !forceTest) {
      setIsFixedErrorState(true);
      setError(`Não foi possível reproduzir o canal após ${MAX_TOTAL_ATTEMPTS} tentativas. Por favor, tente outro canal ou verifique sua conexão.`);
      return;
    }

    setIsTesting(true);
    setUrlTested(true);

    console.log('Testando URL original para canal', channel.id);

    try {
      if (!forceTest) {
        setTotalAttempts(prevAttempts => prevAttempts + 1);
      }

      const originalUrl = channel.url;

      setStreamTestResults({
        success: false,
        message: 'Não foi possível reproduzir o canal. Tente o player alternativo ou abra em um player externo.',
        results: [
          {
            url: originalUrl,
            success: false,
            message: 'Falha ao reproduzir o canal com a URL original.'
          }
        ]
      });

      setIsFixedErrorState(true);
      setError('Não foi possível reproduzir o canal. Tente o player alternativo ou abra em um player externo.');

    } catch (err) {
      console.error('Erro ao testar URL:', err);
      setIsFixedErrorState(true);
    } finally {
      setIsTesting(false);
    }
  }, [channel, totalAttempts]);

  const tryNextFormat = useCallback(() => {
    if (!channel) return;

    if (totalAttempts >= MAX_TOTAL_ATTEMPTS) {
      setIsFixedErrorState(true);
      setError(`Não foi possível reproduzir o canal após ${MAX_TOTAL_ATTEMPTS} tentativas. Por favor, tente outro canal ou verifique sua conexão.`);
      return;
    }

    setTotalAttempts(prevAttempts => prevAttempts + 1);

    setIsLoading(false);
    setError('Não foi possível reproduzir o canal. Tente o player alternativo ou abra em um player externo.');
    setIsFixedErrorState(true);

  }, [channel, totalAttempts]);

  const handleError = (error) => {
    console.error('Player error:', error);
    setError('Ocorreu um erro ao reproduzir o canal. Tente novamente.');
    setIsLoading(false);
  };

  const handleBuffer = () => {
    setIsLoading(true);
  };

  const handleBufferEnd = () => {
    setIsLoading(false);
  };

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    setRetryCount((prev) => prev + 1);
    loadChannel();
  };

  const toggleAlternativePlayer = () => {
    setUseAlternativePlayer((prev) => !prev);
    setError(null);
    setIsLoading(true);
    loadChannel();
  };

  const openInExternalPlayer = () => {
    if (streamUrl) {
      window.open(streamUrl, '_blank');
    }
  };

  const toggleDiagnostics = () => {
    setShowDiagnostics(!showDiagnostics);
  };

  const setSpecificUrl = (url) => {
    if (!url) return;
    setStreamUrl(url);
    setError(null);
    setIsLoading(true);
    setIsFixedErrorState(false);
    setTotalAttempts(0);
  };

  useEffect(() => {
    if (!streamUrl) {
      console.log('Sem URL de stream');
      return;
    }

    if (!streamUrl.includes('.ts')) {
      console.log('URL não é um stream .ts:', streamUrl);
      return;
    }

    if (!videoRef.current) {
      console.log('Elemento de vídeo não está pronto');
      return;
    }

    console.log("Inicializando mpegts.js para:", streamUrl);

    if (mpegts.isSupported()) {
      try {
        // Limpar player anterior
        if (mpegtsPlayerRef.current) {
          console.log('Destruindo player anterior');
          mpegtsPlayerRef.current.destroy();
          mpegtsPlayerRef.current = null;
        }

        console.log('Criando nova instância do player mpegts.js');
        mpegtsPlayerRef.current = mpegts.createPlayer({
          type: 'mpegts',
          url: streamUrl,
          isLive: true,
          enableStashBuffer: false,
          liveBufferLatencyChasing: true,
          liveSync: true,
          stashInitialSize: 128,
          cors: true,
          withCredentials: false,
          headers: {
            'Referer': new URL(streamUrl).origin,
            'Origin': new URL(streamUrl).origin
          }
        });

        console.log('Anexando elemento de vídeo ao player');
        mpegtsPlayerRef.current.attachMediaElement(videoRef.current);

        // Adicionar mais eventos para debug
        mpegtsPlayerRef.current.on(mpegts.Events.ERROR, (errorType, errorDetail) => {
          console.error('Erro mpegts.js:', errorType, errorDetail);
          setError(`Erro ao decodificar stream MPEG-TS: ${errorDetail}`);
        });

        mpegtsPlayerRef.current.on(mpegts.Events.LOADING_COMPLETE, () => {
          console.log('mpegts.js: Carregamento completo');
          setIsLoading(false);
          // Iniciar reprodução após o carregamento se isPlaying for true
          if (isPlaying && videoRef.current) {
            console.log('Iniciando reprodução após carregamento');
            videoRef.current.play().catch(err => {
              console.error('Erro ao iniciar reprodução após carregamento:', err);
            });
          }
        });

        mpegtsPlayerRef.current.on(mpegts.Events.MEDIA_INFO, (mediaInfo) => {
          console.log('mpegts.js: Informações do mídia:', mediaInfo);
        });

        mpegtsPlayerRef.current.on('statistics_info', (statInfo) => {
          console.log('mpegts.js: Estatísticas:', statInfo);
        });

        mpegtsPlayerRef.current.on(mpegts.Events.RECOVERED_EARLY_EOF, () => {
          console.log('mpegts.js: Recuperado de EOF prematuro');
        });

        console.log('Iniciando carregamento do stream');
        mpegtsPlayerRef.current.load();

        if (isPlaying) {
          console.log('Tentando iniciar reprodução automática');
          videoRef.current.play().catch(err => {
            console.error('Erro ao iniciar reprodução automática:', err);
          });
        }
      } catch (err) {
        console.error('Erro ao criar player mpegts:', err);
        setError(`Não foi possível inicializar o player MPEG-TS: ${err.message}`);
      }
    } else {
      console.error('mpegts.js não é suportado neste navegador');
      setError('Seu navegador não suporta reprodução de streams IPTV. Tente um player externo.');
    }

    return () => {
      if (mpegtsPlayerRef.current) {
        console.log('Destruindo player mpegts.js');
        mpegtsPlayerRef.current.destroy();
        mpegtsPlayerRef.current = null;
      }
    };
  }, [streamUrl, isPlaying]);

  return (
    <PlayerContainer>
      <AspectRatioBox>
        {/* Loading Inicial */}
        {initialLoading && (
          <BufferingOverlay>
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#E50914" />
                  <stop offset="50%" stopColor="#FF4D4D" />
                  <stop offset="100%" stopColor="#E50914" />
                </linearGradient>
              </defs>
              <g transform="translate(50,50)">
                <circle r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                <circle r="40" fill="none" stroke="url(#gradient)" strokeWidth="8" strokeLinecap="round" strokeDasharray="251.2" strokeDashoffset="125.6">
                  <animateTransform 
                    attributeName="transform"
                    type="rotate"
                    from="0"
                    to="360"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle r="30" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                <circle r="30" fill="none" stroke="url(#gradient)" strokeWidth="8" strokeLinecap="round" strokeDasharray="188.4" strokeDashoffset="94.2">
                  <animateTransform 
                    attributeName="transform"
                    type="rotate"
                    from="360"
                    to="0"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            </svg>
            <p>Carregando stream...</p>
          </BufferingOverlay>
        )}

        {/* Player de Vídeo */}
        {channel && !isTesting && streamUrl && !streamUrl.includes('.ts') && (
          <ReactPlayer
            ref={playerRef}
            className="react-player"
            url={streamUrl}
            playing={isPlaying}
            controls={false}
            width="100%"
            height="100%"
            volume={volume}
            muted={volume === 0}
            onError={handleError}
            onBuffer={handleBuffer}
            onBufferEnd={handleBufferEnd}
            onProgress={({ played }) => onProgress?.(played)}
            onDuration={onDuration}
            config={{
              file: {
                forceVideo: true,
                attributes: {
                  controlsList: 'nodownload',
                  style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }
                },
                hlsOptions: {
                  enableWorker: true,
                  lowLatencyMode: true,
                  liveSyncDuration: 3,
                  backBufferLength: 90
                }
              }
            }}
          />
        )}

        {/* Player MPEG-TS */}
        {channel && !isTesting && streamUrl && streamUrl.includes('.ts') && (
          <video
            ref={videoRef}
            className="react-player"
            controls={false}
            style={{ 
              width: '100%', 
              height: '100%', 
              position: 'absolute', 
              top: 0, 
              left: 0,
              objectFit: 'contain'
            }}
            playsInline
            autoPlay={isPlaying}
            muted={volume === 0}
            volume={volume}
            onTimeUpdate={(e) => {
              const video = e.target;
              onProgress?.(video.currentTime / video.duration);
            }}
            onLoadedMetadata={(e) => {
              onDuration?.(e.target.duration);
            }}
          />
        )}

        {/* Overlay de Carregamento durante buffering */}
        {isLoading && !initialLoading && !isTesting && (
          <BufferingOverlay>
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#E50914" />
                  <stop offset="50%" stopColor="#FF4D4D" />
                  <stop offset="100%" stopColor="#E50914" />
                </linearGradient>
              </defs>
              <g transform="translate(50,50)">
                <circle r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                <circle r="40" fill="none" stroke="url(#gradient)" strokeWidth="8" strokeLinecap="round" strokeDasharray="251.2" strokeDashoffset="125.6">
                  <animateTransform 
                    attributeName="transform"
                    type="rotate"
                    from="0"
                    to="360"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </circle>
                <circle r="30" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                <circle r="30" fill="none" stroke="url(#gradient)" strokeWidth="8" strokeLinecap="round" strokeDasharray="188.4" strokeDashoffset="94.2">
                  <animateTransform 
                    attributeName="transform"
                    type="rotate"
                    from="360"
                    to="0"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            </svg>
            <p>Carregando stream{retryCount > 0 ? ` (Tentativa ${retryCount}/3)` : ''}...</p>
          </BufferingOverlay>
        )}

        {/* Overlay de Erro */}
        {error && (
          <ErrorOverlay>
            <h3>Erro de Reprodução</h3>
            <p>{error}</p>
            <div className="error-buttons">
              <button onClick={handleRetry}>Tentar Novamente</button>
              <button className="secondary" onClick={toggleAlternativePlayer}>
                {useAlternativePlayer ? 'Usar Player Padrão' : 'Usar Player Alternativo'}
              </button>
              <button className="secondary" onClick={openInExternalPlayer}>
                Abrir em Player Externo
              </button>
            </div>
          </ErrorOverlay>
        )}
      </AspectRatioBox>
    </PlayerContainer>
  );
});

Player.displayName = 'Player';

export default Player;